import React from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HomeWorkOutlined from '@mui/icons-material/HomeWorkOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import SortIcon from '@mui/icons-material/Sort';
import {
  Chip,
  FormControl,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

import { track } from '@klokhuis/core/Analytics/libs/analytics-lib';
import { AnalyticsEvent } from '@klokhuis/core/Analytics/types';
import {
  PropertyAdvertSortOptionID,
  PropertyTypeID,
} from '@klokhuis/core/ApiClient/Klokhuis';
import { useIntl } from '@klokhuis/core/Intl';
import { useFilterSelectors } from '@klokhuis/modules/Properties/context/hooks/useFilterSelectors';
import { useListingSelectors } from '@klokhuis/modules/Properties/context/hooks/useListingSelectors';
import { ListingsContext } from '@klokhuis/modules/Properties/context/ListingsContext';
import { useFilterStateDispatcher } from '@klokhuis/modules/Properties/hooks/useFilterStateDispatcher';
import {
  PropertyAdvertSortOptionIDTranslations,
  PropertyTypeIDPluralTranslations,
} from '@klokhuis/modules/Properties/translations';
import { PropertyAdvertSortOptionIDValues } from '@klokhuis/modules/Properties/types';

import styles from './SortListings.module.css';
import { useMediaIsDesktop } from '@klokhuis/utilities/Media/useMediaIsDesktop';

const SortListingsInternal = () => {
  const { listingsState } = ListingsContext.useContext();
  const listingTypes =
    listingsState.filterResult.filters.listingTypeIds || null;

  const { T } = useIntl();
  const isDesktop = useMediaIsDesktop();
  const { filterStateDispatcher } = useFilterStateDispatcher();

  const cityName = useFilterSelectors().getCityName();
  const isValidCity = useFilterSelectors().isValidCity();
  const listingCount = useListingSelectors().getListingCount();

  const getTranslatedPropertyType = (
    types: PropertyTypeID[] | null,
  ): string => {
    if (!types || types.length !== 1) {
      return T('listings_page.listing_count.generic');
    }
    if (types[0] && types[0] in PropertyTypeIDPluralTranslations) {
      return T(PropertyTypeIDPluralTranslations[types[0]]);
    }
    return T('listings_page.listing_count.generic');
  };

  const translatedType = getTranslatedPropertyType(listingTypes);

  const headingText =
    isValidCity && cityName
      ? T(
          'listings_page.search_header.title_with_city',
          cityName.split(',')[0],
          listingCount,
          translatedType,
        )
      : T(
          'listings_page.search_header.title_no_city',
          listingCount,
          translatedType,
        );

  const handleSortChange = React.useCallback(
    (event: SelectChangeEvent<PropertyAdvertSortOptionID>) => {
      track(AnalyticsEvent.SearchSortApplied);
      const newSortValue = event.target.value as PropertyAdvertSortOptionID;
      filterStateDispatcher({
        ...listingsState.filterResult.filters,
        listingSortOptionId: newSortValue,
        pageNo: 1,
      });
    },
    [filterStateDispatcher, listingsState.filterResult.filters],
  );

  const renderValue = (value: unknown) => (
    <Typography variant="body3" className={styles.sortLabel}>
      {T(
        PropertyAdvertSortOptionIDTranslations[
          value as PropertyAdvertSortOptionID
        ],
      )}
    </Typography>
  );

  return (
    <div className={styles.root}>
      <div className={styles.headerSection}>
        {/* <div className={styles.chipRow}>
          {isValidCity && cityName && (
            <Chip
              icon={<LocationOnOutlined className={styles.chipIcon} />}
              label={cityName}
              size="small"
              className={styles.chip}
            />
          )}
          {listingTypes?.length === 1 && (
            <Chip
              icon={<HomeWorkOutlined className={styles.chipIcon} />}
              label={translatedType}
              size="small"
              className={styles.chip}
            />
          )}
        </div> */}

        <Typography
          variant={isDesktop ? 'h4' : 'h5'}
          className={styles.searchResultHeader}
        >
          {headingText}
        </Typography>

        <Typography variant="subtitle2" className={styles.headerDescription}>
          {T('listings_page.search_header.subtitle')}
        </Typography>
      </div>

      <div className={styles.sortDropdown}>
        <FormControl
          variant="outlined"
          size="small"
          className={styles.formControl}
        >
          <Select
            className={styles.select}
            value={
              listingsState.filterResult.filters.listingSortOptionId ||
              PropertyAdvertSortOptionID.NewestFirst
            }
            label={T(
              PropertyAdvertSortOptionIDTranslations[
                PropertyAdvertSortOptionID.NewestFirst
              ],
            )}
            autoWidth
            size="small"
            input={
              <OutlinedInput
                size="small"
                startAdornment={
                  <InputAdornment position="start">
                    <SortIcon className={styles.sortIcon} />
                  </InputAdornment>
                }
              />
            }
            renderValue={renderValue}
            IconComponent={KeyboardArrowDownIcon}
            onChange={handleSortChange}
          >
            {PropertyAdvertSortOptionIDValues.map(sortOption => (
              <MenuItem key={sortOption} value={sortOption}>
                <Typography variant="body3" className={styles.menuItem}>
                  {T(PropertyAdvertSortOptionIDTranslations[sortOption])}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );
};
export const SortListings = React.memo(SortListingsInternal);
